/**
 * The examiner's file beside a live call: what it is reading now, every
 * record it has read in this interview, and what else its file holds
 * (`deskOf`, `ProbeDesk.logic.ts`). Records open in a new tab, so the call
 * keeps running.
 *
 * @module components/apps/probe/ProbeDesk
 */
import type { ProbeBriefing } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import {
  ChevronRightIcon,
  FileStackIcon,
  FileTextIcon,
  GlobeIcon,
  HistoryIcon,
  LandmarkIcon,
  LibraryIcon,
  LoaderCircleIcon,
  NewspaperIcon,
  SearchXIcon,
  CheckIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from "lucide-react";
import { memo, useId, useState, type ReactNode } from "react";

import { safeExternalHref } from "~/lib/safeExternalHref";
import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import {
  deskOf,
  deskReadCount,
  type ProbeDesk as ProbeDeskShape,
  type ProbeDeskCall,
  type ProbeDeskCard,
  type ProbeDeskEntry,
  type ProbeDeskFile,
  type ProbeDeskFileLeft,
  type ProbeDeskFileSection,
  type ProbeDeskLink,
  type ProbeDeskRow,
} from "./ProbeDesk.logic";

export const KIND_ICON: Readonly<Record<ProbeDeskCard["kind"], LucideIcon>> = {
  finding: TriangleAlertIcon,
  policy: FileTextIcon,
  update: NewspaperIcon,
  passage: LandmarkIcon,
  rehearsal: HistoryIcon,
  web: GlobeIcon,
};

export const FILE_SECTIONS: Readonly<
  Record<ProbeDeskFileSection, { readonly label: string; readonly icon: LucideIcon }>
> = {
  findings: { label: "Open findings", icon: TriangleAlertIcon },
  updates: { label: "Regulator changes", icon: NewspaperIcon },
  authority: { label: "Authority passages", icon: LandmarkIcon },
  library: { label: "Policy library", icon: LibraryIcon },
};

const ROWS_PREVIEW = 8;

export const DESK_LABEL_CLASS = "text-muted-foreground dark:text-foreground/50";

export const LINKED_CLASS =
  "outline-none transition-colors hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring/70";

export const ProbeDesk = memo(function ProbeDesk({
  desk,
  orgSegment,
  headingId,
}: {
  readonly desk: ProbeDeskShape;
  readonly orgSegment: string;
  /** The sheet labels itself by the desk's own heading rather than a second, hidden one. */
  readonly headingId?: string;
  readonly onShowTurn?: (turnKey: string) => void;
}) {
  const id = useId();
  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 [&>*]:shrink-0"
      data-probe-desk=""
    >
      <div>
        <h2 id={headingId} className="text-xs font-medium text-foreground">
          The examiner&rsquo;s file
        </h2>
        <p className={cn("text-xs", DESK_LABEL_CLASS)}>What it has read, and what is left</p>
      </div>

      {desk.reading.length > 0 ? (
        <section
          aria-labelledby={`${id}-reading`}
          className="rounded-lg border border-border/60 bg-card px-3 py-2.5"
          data-probe-desk-reading=""
        >
          <div className={cn("flex items-center gap-1.5 text-[11px]", DESK_LABEL_CLASS)}>
            <LoaderCircleIcon
              aria-hidden
              className="size-3 text-foreground motion-safe:animate-spin"
            />
            <h3 id={`${id}-reading`} className="text-foreground">
              Reading now
            </h3>
          </div>
          <ul className="mt-1 flex flex-col gap-1.5">
            {desk.reading.map((reading) => (
              <li key={reading.callId}>
                <p className="text-[12.5px] font-medium leading-snug">{reading.title}</p>
                {reading.detail !== null ? (
                  <p className={cn("text-xs", DESK_LABEL_CLASS)}>{reading.detail}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ProbeDeskRead
        read={desk.read}
        heading="Read in this interview"
        empty="Nothing yet. What the examiner opens or searches shows here."
        orgSegment={orgSegment}
      />

      {desk.file !== null ? (
        <FileLeft file={desk.file} headingId={`${id}-file`} orgSegment={orgSegment} />
      ) : null}
    </div>
  );
});

export interface ProbeDeskRenderProps {
  readonly orgSegment: string;
  /** The sheet labels itself by the desk's own heading rather than a second, hidden one. */
  readonly headingId?: string;
  /** Takes the reader to a line of the transcript, by its `ProbeDeskTurn` key. */
  readonly onShowTurn?: (turnKey: string) => void;
}

/** A desk drawn for one call: how many things it counts as read, and the desk itself. */
export interface ProbeDeskView<Props> {
  readonly read: number;
  readonly render: (props: Props) => ReactNode;
}

/**
 * The examiner's file as a page draws it for one call, live or saved: the
 * panel beside the conversation or in the sheet, and the count on the
 * sheet's button.
 */
export type ProbeStageDesk = (
  input: ProbeDeskCall,
  briefing: ProbeBriefing | null,
) => ProbeDeskView<ProbeDeskRenderProps>;

export const probeStageDesk: ProbeStageDesk = (input, briefing) => {
  const desk = deskOf(input, briefing);
  return { read: deskReadCount(desk), render: (props) => <ProbeDesk desk={desk} {...props} /> };
};

export function ProbeDeskButton({
  read,
  onClick,
  className,
}: {
  readonly read: number;
  readonly onClick: () => void;
  readonly className?: string;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn("cursor-pointer", className)}
      onClick={onClick}
      data-probe-desk-open=""
    >
      <FileStackIcon className="size-3.5" />
      {/* On a phone the label would run into the orb; the button keeps its name. */}
      <span className="@max-md/stage:sr-only">Examiner&rsquo;s file</span>
      {read > 0 ? (
        <>
          {" "}
          <span className={cn("tabular-nums", DESK_LABEL_CLASS)}>
            {read}
            <span className="sr-only"> read</span>
          </span>
        </>
      ) : null}
    </Button>
  );
}

const entryKey = (entry: ProbeDeskEntry) => (entry.kind === "card" ? entry.card.key : entry.key);

export function ProbeDeskRead({
  read,
  heading,
  empty,
  orgSegment,
}: {
  readonly read: ProbeDeskShape["read"];
  /** The saved page names the records itself, so its desk draws no heading. */
  readonly heading?: string;
  readonly empty: string;
  readonly orgSegment: string;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={heading === undefined ? undefined : id}
      className="flex flex-col gap-2"
      data-probe-desk-read=""
    >
      {heading !== undefined ? <DeskHeading id={id}>{heading}</DeskHeading> : null}
      {read.length === 0 ? (
        <Quiet>{empty}</Quiet>
      ) : (
        <ol className="flex flex-col gap-2">
          {read.map((entry) => (
            <li key={entryKey(entry)}>
              <Entry entry={entry} orgSegment={orgSegment} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function DeskHeading({ id, children }: { readonly id: string; readonly children: ReactNode }) {
  return (
    <h3 id={id} className={cn("pt-1 text-[11px] font-medium", DESK_LABEL_CLASS)}>
      {children}
    </h3>
  );
}

function Quiet({ children }: { readonly children: ReactNode }) {
  return (
    <p
      className={cn(
        "rounded-lg border border-dashed border-border/70 px-3 py-2.5 text-xs",
        DESK_LABEL_CLASS,
      )}
    >
      {children}
    </p>
  );
}

function Entry({
  entry,
  orgSegment,
}: {
  readonly entry: ProbeDeskEntry;
  readonly orgSegment: string;
}) {
  switch (entry.kind) {
    case "card":
      return <Card card={entry.card} orgSegment={orgSegment} />;
    case "list":
      return <ListRow entry={entry} orgSegment={orgSegment} />;
    case "nothing":
      return (
        <div
          className={cn(
            "rounded-lg border border-border/60 px-3 py-2 text-[12.5px]",
            DESK_LABEL_CLASS,
          )}
          data-probe-desk-nothing=""
        >
          <p className="flex items-center gap-2">
            {entry.checked ? (
              <CheckIcon aria-hidden className="size-3.5 shrink-0" />
            ) : (
              <SearchXIcon aria-hidden className="size-3.5 shrink-0" />
            )}
            <span className="min-w-0 flex-1">{entry.title}</span>
          </p>
          {entry.query !== null ? (
            <p className="mt-0.5 line-clamp-2 ps-5.5 text-xs">{entry.query}</p>
          ) : null}
        </div>
      );
    case "failed":
      return (
        <div
          className="rounded-lg border border-border/60 bg-card px-3 py-2.5"
          data-probe-desk-failed=""
        >
          <div className={cn("flex items-center gap-1.5 text-[11px]", DESK_LABEL_CLASS)}>
            <TriangleAlertIcon aria-hidden className="size-3 text-warning" />
            <span>Couldn&rsquo;t read</span>
          </div>
          <p className="mt-1 text-[12.5px] font-medium leading-snug">{entry.title}</p>
          {entry.reason !== null ? (
            <p className={cn("mt-0.5 line-clamp-3 text-xs", DESK_LABEL_CLASS)}>{entry.reason}</p>
          ) : null}
        </div>
      );
  }
}

export function RecordLink({
  link,
  orgSegment,
  className,
  children,
}: {
  readonly link: ProbeDeskLink | null;
  readonly orgSegment: string;
  readonly className: string;
  readonly children: ReactNode;
}) {
  if (link === null) return <div className={className}>{children}</div>;
  const linked = cn(className, LINKED_CLASS);
  const opened = (
    <>
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </>
  );
  switch (link.kind) {
    case "finding":
      return (
        <Link
          to="/$org/compliance/findings/$findingId"
          params={{ org: orgSegment, findingId: link.id }}
          target="_blank"
          data-probe-finding={link.id}
          className={linked}
        >
          {opened}
        </Link>
      );
    case "policy":
      return (
        <Link
          to="/$org/compliance/policies/$policyId"
          params={{ org: orgSegment, policyId: link.id }}
          target="_blank"
          data-probe-policy={link.id}
          className={linked}
        >
          {opened}
        </Link>
      );
    case "url": {
      const href = safeExternalHref(link.url);
      return href === null ? (
        <div className={className}>{children}</div>
      ) : (
        <a href={href} target="_blank" rel="noreferrer" className={linked}>
          {opened}
        </a>
      );
    }
  }
}

function Card({ card, orgSegment }: { readonly card: ProbeDeskCard; readonly orgSegment: string }) {
  const Icon = KIND_ICON[card.kind];
  return (
    <RecordLink
      link={card.link}
      orgSegment={orgSegment}
      className="block rounded-lg border border-border/60 bg-card px-3 py-2.5"
    >
      <span className={cn("flex items-center gap-1.5 text-[11px]", DESK_LABEL_CLASS)}>
        <Icon aria-hidden className="size-3 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{card.label}</span>
      </span>
      <span className="mt-1 block text-[12.5px] font-medium leading-snug">{card.title}</span>
      {card.detail !== null ? (
        <span
          className={cn(
            "mt-0.5 block text-xs",
            DESK_LABEL_CLASS,
            card.kind === "passage" || card.kind === "policy" ? "line-clamp-3" : "line-clamp-2",
          )}
        >
          {card.detail}
        </span>
      ) : null}
    </RecordLink>
  );
}

const rowOfCard = (card: ProbeDeskCard): ProbeDeskRow => ({
  key: card.key,
  title: card.title,
  note: card.severity,
  link: card.link,
});

function ListRow({
  entry,
  orgSegment,
}: {
  readonly entry: Extract<ProbeDeskEntry, { kind: "list" }>;
  readonly orgSegment: string;
}) {
  const label = (
    <>
      <span className="min-w-0 flex-1">Looked through {entry.subject}</span>
      {entry.count !== null ? <span className="shrink-0 tabular-nums">{entry.count}</span> : null}
    </>
  );
  return (
    <div className="overflow-hidden rounded-lg border border-border/60" data-probe-desk-list="">
      {entry.cards.length > 0 ? (
        <Expandable rows={entry.cards.map(rowOfCard)} orgSegment={orgSegment}>
          {label}
        </Expandable>
      ) : (
        <p className={cn("flex items-center gap-2 px-3 py-2 text-[12.5px]", DESK_LABEL_CLASS)}>
          {label}
        </p>
      )}
    </div>
  );
}

function Expandable({
  rows,
  unlisted = 0,
  orgSegment,
  children,
}: {
  readonly rows: ReadonlyArray<ProbeDeskRow>;
  readonly unlisted?: number;
  readonly orgSegment: string;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const rowsId = useId();
  const shown = all ? rows : rows.slice(0, ROWS_PREVIEW);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-controls={rowsId}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start text-[12.5px] text-foreground",
          LINKED_CLASS,
        )}
      >
        <ChevronRightIcon
          aria-hidden
          className={cn("size-3.5 shrink-0 motion-safe:transition-transform", open && "rotate-90")}
        />
        {children}
      </button>
      <ul
        id={rowsId}
        hidden={!open}
        className="divide-y divide-border/50 border-t border-border/50 bg-card"
      >
        {shown.map((row) => (
          <li key={row.key}>
            <RecordLink
              link={row.link}
              orgSegment={orgSegment}
              className="flex items-baseline gap-2 px-3 py-1.5"
            >
              <span className="min-w-0 flex-1 truncate text-xs">{row.title}</span>
              {row.note !== null ? (
                <span
                  className={cn("shrink-0 text-[11px] first-letter:uppercase", DESK_LABEL_CLASS)}
                >
                  {row.note}
                </span>
              ) : null}
            </RecordLink>
          </li>
        ))}
        {rows.length > ROWS_PREVIEW ? (
          <li>
            <button
              type="button"
              onClick={() => setAll((was) => !was)}
              aria-expanded={all}
              aria-controls={rowsId}
              className={cn(
                "w-full cursor-pointer px-3 py-1.5 text-start text-xs",
                DESK_LABEL_CLASS,
                LINKED_CLASS,
              )}
            >
              {all ? "Show fewer" : `Show all ${rows.length}`}
            </button>
          </li>
        ) : null}
        {unlisted > 0 ? (
          <li className={cn("px-3 py-1.5 text-xs", DESK_LABEL_CLASS)}>
            And {unlisted} more the examiner looks up during the call
          </li>
        ) : null}
      </ul>
    </>
  );
}

function FileLeft({
  file,
  headingId,
  orgSegment,
}: {
  readonly file: ProbeDeskFile;
  readonly headingId: string;
  readonly orgSegment: string;
}) {
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <DeskHeading id={headingId}>Also in the file</DeskHeading>
      {file.empty ? (
        <Quiet>The file is empty. No findings, regulator changes, passages or policies.</Quiet>
      ) : file.sections.length === 0 ? (
        <Quiet>The examiner has read everything in its file.</Quiet>
      ) : (
        <ul className="divide-y divide-border/50 overflow-hidden rounded-lg border border-border/60">
          {file.sections.map((left) => (
            <li key={left.section} data-probe-desk-file={left.section}>
              <FileSection left={left} lessRead={file.lessRead} orgSegment={orgSegment} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FileSection({
  left: { section, count, rows, unlisted },
  lessRead,
  orgSegment,
}: {
  readonly left: ProbeDeskFileLeft;
  readonly lessRead: boolean;
  readonly orgSegment: string;
}) {
  const { label, icon: Icon } = FILE_SECTIONS[section];
  return (
    <Expandable rows={rows} unlisted={unlisted} orgSegment={orgSegment}>
      <Icon aria-hidden className="size-3.5 shrink-0" />
      <span className="flex-1">{label}</span>
      <span className="tabular-nums">{lessRead ? `${count} unread` : count}</span>
    </Expandable>
  );
}
